import React from "react";
import { motion } from "framer-motion";
import { pageVariants } from "../animations/pageTransitions";
import "./Legals.css";

/**
 * Quantisys360 — Costs & Certification Information
 *
 * Public ONC mandatory disclosure page.
 * Keep this page publicly accessible and linked from the website footer.
 * Update placeholders after certification is issued.
 */

const CERTIFICATION_INFO = {
  developerOrganizationName: "S&S Healthcare Consultancy LLC",
  productName: "Quantisys360",
  productVersion: "v1.0",
  dateCertified: "TBD (Will be updated upon certification issuance)",
  uniqueCertificationNumber: "TBD (Will be updated upon certification issuance)",
};

const CERTIFICATION_CRITERIA = [
  "170.315 (d)(12): Encrypt Authentication Credentials (ONC Test Method; Version 1.0)",
  "170.315 (d)(13): Multi-Factor Authentication (ONC Test Method; Version 1.1)",
  "170.315 (g)(4): Quality Management System (ONC Test Method; Version 1.0)",
  "170.315 (g)(5): Accessibility-centered design (ONC Test Method; Version 1.2)",
  "170.315 (b)(10): Electronic Health Information Export (ONC Test Method; Version 1.2)",
];

const CQMS = [
  "No CQMs certified for this Health IT Module at this time.",
];

const RELIED_UPON_SOFTWARE = [
  "Third-party clearinghouse services may be required for certain workflows such as claims processing.",
  "Third-party body mapping component provided by BioDigital Human used for clinical documentation and visualization",
  "Additional third-party integrations may be required depending on the certified capabilities enabled for a customer.",
];

const ONC_DISCLAIMER =
  "This Health IT Module is compliant with the ONC Certification Criteria for Health IT and has been certified by an ONC-ACB in accordance with the applicable certification criteria adopted by the Secretary of Health and Human Services. This certification does not represent an endorsement by the U.S. Department of Health and Human Services.";

const CLEARINGHOUSE_NAME = "TBD (Third-Party Clearinghouse Provider)";

const LINKS = {
  ehiExportDocs: "/ehi",
  realWorldTestPlan: "/real-world-testing",
};

const QuantisysCostsAndCertification = () => {
  const {
    developerOrganizationName,
    productName,
    productVersion,
    dateCertified,
    uniqueCertificationNumber,
  } = CERTIFICATION_INFO;

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="legal-container">
        <h1>Costs and Certification Information</h1>

        <p className="legal-note">
          This page provides information about <strong>costs</strong> associated with certified
          health IT capabilities of <strong>{productName}</strong> and includes required product
          certification information in plain language.
        </p>

        <h2>Product Overview</h2>
        <div className="legal-highlight">
          <p>
            <strong>Developer Name:</strong> {developerOrganizationName}
          </p>
          <p>
            <strong>Product Name:</strong> {productName}
          </p>
          <p>
            <strong>Version:</strong> {productVersion}
          </p>
          <p>
            <strong>Description:</strong> {productName} is a care delivery and EHR platform designed
            to support clinical workflows, administrative tasks, patient engagement, and interoperability
            needs for medical practices.
          </p>
        </div>

        <h2>Certified Product Information</h2>
        <div className="legal-highlight">
          <p>
            <strong>Date Certified:</strong> {dateCertified}
          </p>
          <p>
            <strong>Unique Certification Number:</strong> {uniqueCertificationNumber}
          </p>
        </div>

        <h2>Certification Criteria</h2>
        <div className="legal-highlight">
          {CERTIFICATION_CRITERIA.map((item, idx) => (
            <p key={idx}>✔ {item}</p>
          ))}
        </div>

        <h2>Clinical Quality Measures (CQMs)</h2>
        <div className="legal-highlight">
          {CQMS.map((item, idx) => (
            <p key={idx}>✔ {item}</p>
          ))}
        </div>

        <h2>Additional Software Relied Upon</h2>
        <div className="legal-highlight">
          {RELIED_UPON_SOFTWARE.map((item, idx) => (
            <p key={idx}>✔ {item}</p>
          ))}
          <p>
            <strong>Clearinghouse:</strong> {CLEARINGHOUSE_NAME}
          </p>
        </div>

        <h2>Disclaimer</h2>
        <p className="legal-note">{ONC_DISCLAIMER}</p>

        <h2>Costs</h2>
        <p>
          Quantisys360 is offered on a monthly subscription basis. Costs and fees may vary based on
          the number of providers or users, selected features, interfaces, implementation needs, and
          any required third-party services.
        </p>

        <div className="legal-highlight">
          <p>
            <strong>Monthly Subscription Fees:</strong> Monthly fees per provider may cover access to the
            software, customer support, updates, and ongoing maintenance.
          </p>
          <p>
            <strong>Implementation and Setup Fees:</strong> A one-time implementation fee may apply for
            initial setup, configuration, onboarding, and workflow alignment.
          </p>
          <p>
            <strong>Training:</strong> Training may be included with onboarding or offered as an additional
            service depending on customer needs.
          </p>
          <p>
            <strong>Data Migration and Conversion:</strong> If transitioning from a prior EHR, a one-time
            fee may apply for data conversion or migration depending on data volume and complexity.
          </p>
          <p>
            <strong>Interfaces and Integrations:</strong> If a customer requires interfaces to external
            systems such as labs, registries, HIEs, or other services, one-time setup fees and/or ongoing
            fees may apply depending on the integration and usage.
          </p>
          <p>
            <strong>Third-Party Vendor Fees:</strong> Some certified workflows may require third-party
            services. Those third-party vendors may charge one-time, recurring, monthly, annual, or
            transaction-based fees.
          </p>
        </div>

        <h3>Third-Party Services and Additional Fees</h3>
        <p className="legal-note">
          Some workflows may require third-party services. For example, claims processing may require
          a third-party clearinghouse provider. Depending on the service used, the provider or practice
          may need to establish an agreement directly with that vendor.
        </p>

        <div className="legal-highlight">
          <p>
            <strong>Clearinghouse Services:</strong> {CLEARINGHOUSE_NAME} (vendor fees may apply).
          </p>
          <p>
            <strong>Vendor Agreements:</strong> Depending on the vendor service used, a provider or
            practice may be required to establish a separate agreement directly with the vendor.
          </p>

          <p><strong>Additional Software Relied Upon:</strong> Third-party body mapping component provided by 
              BioDigital Human used for clinical documentation and visualization.</p>

        </div>

        <p className="legal-note">
          Unless otherwise specified, certified functionality is included within the standard subscription
          and does not require additional fees beyond the subscription and implementation costs described above.
        </p>

        <p className="legal-note">
          If there are no additional costs associated with a particular certified capability beyond the
          subscription fees described above, that capability is included at no additional cost.
        </p>

        <p className="legal-contact">
          For questions about costs or to request a quote, contact us at{" "}
          <a href="mailto:contact@sshealthcareconsulting.com">
            contact@sshealthcareconsulting.com
          </a>.
        </p>

        <h2>Multi-Factor Authentication (MFA)</h2>
        <p>
          Quantisys360 supports multi-factor authentication (MFA), which adds an additional verification
          step during sign-in to help protect accounts if a password is compromised.
        </p>

        <div className="legal-highlight">
          <p>
            ✔ <strong>Enable MFA:</strong> Administrators and/or users can enable MFA based on role and
            organizational settings.
          </p>
          <p>
            ✔ <strong>Login with MFA:</strong> Users sign in with a username and password and complete an
            additional verification step, such as a one-time passcode.
          </p>
          <p>
            ✔ <strong>Disable MFA:</strong> MFA can be disabled by authorized users or administrators
            according to organizational policy. Disabling MFA is not recommended.
          </p>
        </div>

        <h2>Electronic Health Information (EHI) Export</h2>
        <p>
          Quantisys360 supports export of electronic health information (EHI) for individual patients and
          patient populations in accordance with the capabilities enabled for the product.
        </p>

        <p className="legal-note">
          Public documentation for EHI export is available here:{" "}
          <a href={LINKS.ehiExportDocs}>View EHI Export Documentation</a>
        </p>

        <h2>Additional Information</h2>
        <p className="legal-note">
          Real World Testing Plan:{" "}
          <a href={LINKS.realWorldTestPlan}>View Real World Testing Plan</a>
        </p>

        <h2>Contact Information</h2>
        <div className="legal-highlight">
          <p>
            <strong>{developerOrganizationName}</strong>
          </p>
          <p>
            Email:{" "}
            <a href="mailto:contact@sshealthcareconsulting.com">
              contact@sshealthcareconsulting.com
            </a>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default QuantisysCostsAndCertification;